import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Badge,
  Box,
  Button as ChakraButton,
  Flex,
  Grid,
  HStack,
  Icon,
  IconButton,
  Input,
  Select,
  SimpleGrid,
  Spinner,
  Text,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  IoPencilOutline,
  IoSearchSharp,
  IoShieldCheckmarkOutline,
  IoTrashOutline,
} from 'react-icons/io5';
import { MdOutlineChecklist, MdOutlinePeopleAlt } from 'react-icons/md';
import { RiSuitcaseLine } from 'react-icons/ri';
import { useNavigate } from 'react-router-dom';
import Button from '../components/Form/Button';
import HeaderSideBar from '../components/HeaderSideBar';
import { useAuth } from '../contexts/AuthContext';
import { RoleDTO } from '../dtos';
import api from '../services/api';
import { RoleStatus } from '../utils/roleStatus';

const permissionSections = [
  { key: 'home_page', label: 'Home' },
  { key: 'cargo_page', label: 'Cargo' },
  { key: 'equipe_page', label: 'Equipe' },
  { key: 'eleitor_page', label: 'Apoiador' },
  { key: 'demandas_page', label: 'Demanda' },
  { key: 'tarefas_page', label: 'Tarefa' },
  { key: 'mensageria_page', label: 'Mensageria' },
] as const;

type PermissionKey = (typeof permissionSections)[number]['key'];

function getRoleStatusLabel(id?: number) {
  switch (id) {
    case RoleStatus.desativado:
      return 'Desativado';
    case RoleStatus.leitor:
      return 'Leitura';
    case RoleStatus.editor:
      return 'Edicao';
    default:
      return 'Sem acesso';
  }
}

function getRoleStatusColor(id?: number) {
  switch (id) {
    case RoleStatus.editor:
      return {
        bg: '#DCFCE7',
        color: '#166534',
      };
    case RoleStatus.leitor:
      return {
        bg: '#E0F2FE',
        color: '#075985',
      };
    default:
      return {
        bg: '#F1F5F9',
        color: '#475569',
      };
  }
}

export default function Roles() {
  const navigate = useNavigate();
  const toast = useToast();
  const { role, office } = useAuth();
  const cancelRef = useRef() as React.MutableRefObject<HTMLInputElement>;
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<RoleDTO[]>([]);
  const [roleToDeleteId, setRoleToDeleteId] = useState('');
  const [search, setSearch] = useState('');
  const [permissionFilter, setPermissionFilter] = useState<'all' | PermissionKey>('all');

  const canManage = role?.cargo_page > 1;

  const getRoles = useCallback(async () => {
    setLoading(true);

    try {
      const response = await api.get(`/role/office/${office?.id}`);
      setData(response.data);
    } catch (error) {
      console.error('Failed to load roles', error);
    } finally {
      setLoading(false);
    }
  }, [office?.id]);

  useEffect(() => {
    if (office?.id) {
      getRoles();
    }
  }, [getRoles, office?.id]);

  const filteredRoles = useMemo(() => {
    return data.filter(currentRole => {
      const matchesSearch =
        !search.trim() ||
        currentRole.name?.toLowerCase().includes(search.trim().toLowerCase());

      const matchesPermission =
        permissionFilter === 'all' ||
        Number(currentRole[permissionFilter] || 0) > RoleStatus.desativado;

      return matchesSearch && matchesPermission;
    });
  }, [data, permissionFilter, search]);

  const metrics = useMemo(() => {
    const total = data.length;
    const editable = data.filter(item =>
      permissionSections.some(section => Number(item[section.key] || 0) === RoleStatus.editor),
    ).length;
    const reading = data.filter(item =>
      permissionSections.some(section => Number(item[section.key] || 0) === RoleStatus.leitor),
    ).length;
    const protectedRoles = data.filter(item => item.name === 'Proprietário').length;

    return { total, editable, reading, protectedRoles };
  }, [data]);

  const openDialog = (id: string) => {
    setRoleToDeleteId(id);
    onOpen();
  };

  const deleteRole = async () => {
    setLoading(true);

    try {
      await api.delete(`/role/${roleToDeleteId}`);
      toast({
        title: 'Cargo excluido com sucesso',
        status: 'success',
        position: 'top-right',
        duration: 3000,
        isClosable: true,
      });
      setRoleToDeleteId('');
      getRoles();
    } catch (err: any) {
      toast({
        title:
          err?.response?.data?.message ||
          'Ocorreu um erro ao excluir o cargo, tente novamente',
        status: 'error',
        position: 'top-right',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
      onClose();
    }
  };

  return (
    <HeaderSideBar>
      <AlertDialog
        leastDestructiveRef={cancelRef}
        isOpen={isOpen}
        onClose={onClose}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent mx="12px">
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Deseja excluir este cargo?
            </AlertDialogHeader>
            <AlertDialogBody>
              Essa acao e irreversivel. Ao excluir, o cargo nao podera ser recuperado.
            </AlertDialogBody>
            <AlertDialogFooter>
              <ChakraButton onClick={onClose}>Cancelar</ChakraButton>
              <ChakraButton colorScheme="red" ml={3} isLoading={loading} onClick={deleteRole}>
                Excluir
              </ChakraButton>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      <Flex direction="column" gap="16px" pb="24px">
        <Box
          borderRadius="24px"
          px={{ base: '20px', md: '28px' }}
          py={{ base: '22px', md: '28px' }}
          bgGradient={`linear(135deg, ${office?.primary_color || '#0F766E'} 0%, ${
            office?.secondary_color || '#0F172A'
          } 100%)`}
          color="white"
          boxShadow="0 24px 80px rgba(15, 23, 42, 0.18)"
        >
          <Flex justify="space-between" gap="16px" direction={{ base: 'column', lg: 'row' }}>
            <Box maxW="680px">
              <Badge
                bg="rgba(255,255,255,0.14)"
                color="white"
                borderRadius="full"
                px="12px"
                py="4px"
                mb="12px"
              >
                Governanca de acesso
              </Badge>
              <Text fontSize={{ base: '24px', md: '32px' }} fontWeight="700">
                Cargos e niveis de permissao
                {loading && <Spinner color="white" ml="3" size="sm" />}
              </Text>
              <Text mt="8px" color="rgba(255,255,255,0.82)" maxW="560px">
                Estruture os papeis do gabinete com clareza, evitando excesso de acesso e
                facilitando a montagem da equipe.
              </Text>
            </Box>

            {canManage && (
              <Button onClick={() => navigate('/cargo/registrar-cargo')} w={{ base: '100%', lg: '260px' }}>
                Cadastrar cargo
              </Button>
            )}
          </Flex>
        </Box>

        <SimpleGrid columns={{ base: 1, md: 2, xl: 4 }} spacing="16px">
          {[
            { label: 'Cargos cadastrados', value: metrics.total, icon: RiSuitcaseLine },
            { label: 'Com edicao ativa', value: metrics.editable, icon: IoShieldCheckmarkOutline },
            { label: 'Com leitura ativa', value: metrics.reading, icon: MdOutlineChecklist },
            { label: 'Cargos protegidos', value: metrics.protectedRoles, icon: MdOutlinePeopleAlt },
          ].map(item => (
            <Box
              key={item.label}
              bg="white"
              borderRadius="20px"
              p="18px"
              border="1px solid #E2E8F0"
              boxShadow="0 20px 60px rgba(15, 23, 42, 0.08)"
            >
              <Flex justify="space-between" align="center" mb="14px">
                <Text fontSize="13px" color="#64748B" fontWeight="600">
                  {item.label}
                </Text>
                <Icon as={item.icon} fontSize="18px" color="#0F766E" />
              </Flex>
              <Text fontSize="34px" lineHeight="1" fontWeight="700" color="#0F172A">
                {item.value}
              </Text>
            </Box>
          ))}
        </SimpleGrid>

        <Box
          bg="white"
          borderRadius="24px"
          p={{ base: '18px', md: '22px' }}
          border="1px solid #E2E8F0"
          boxShadow="0 20px 60px rgba(15, 23, 42, 0.08)"
        >
          <Flex
            justify="space-between"
            gap="12px"
            direction={{ base: 'column', lg: 'row' }}
            align={{ base: 'stretch', lg: 'center' }}
          >
            <Box>
              <Text fontSize="20px" fontWeight="700" color="#0F172A">
                Biblioteca de cargos
              </Text>
              <Text fontSize="13px" color="#64748B">
                Busque por nome ou filtre por paginas que possuem algum acesso liberado.
              </Text>
            </Box>

            <Flex gap="10px" direction={{ base: 'column', md: 'row' }}>
              <Box position="relative">
                <Input
                  value={search}
                  onChange={event => setSearch(event.target.value)}
                  placeholder="Buscar cargo"
                  pl="38px"
                  w={{ base: '100%', md: '260px' }}
                  borderColor="#CBD5E1"
                  bg="#F8FAFC"
                />
                <Icon
                  as={IoSearchSharp}
                  position="absolute"
                  left="12px"
                  top="50%"
                  transform="translateY(-50%)"
                  color="#64748B"
                />
              </Box>
              <Select
                value={permissionFilter}
                onChange={event => setPermissionFilter(event.target.value as 'all' | PermissionKey)}
                w={{ base: '100%', md: '220px' }}
                borderColor="#CBD5E1"
                bg="#F8FAFC"
              >
                <option value="all">Todas as paginas</option>
                {permissionSections.map(section => (
                  <option key={section.key} value={section.key}>
                    {section.label}
                  </option>
                ))}
              </Select>
            </Flex>
          </Flex>
        </Box>

        <Grid templateColumns={{ base: '1fr', xl: 'repeat(2, minmax(0, 1fr))' }} gap="16px">
          {filteredRoles.length > 0 ? (
            filteredRoles.map(roleItem => (
              <Box
                key={roleItem.id}
                bg="white"
                borderRadius="24px"
                p={{ base: '18px', md: '22px' }}
                border="1px solid #E2E8F0"
                boxShadow="0 20px 60px rgba(15, 23, 42, 0.08)"
              >
                <Flex justify="space-between" gap="12px" mb="18px">
                  <Box>
                    <Flex align="center" gap="10px" wrap="wrap">
                      <Text fontSize="22px" fontWeight="700" color="#0F172A">
                        {roleItem.name}
                      </Text>
                      {roleItem.name === 'Proprietário' && (
                        <Badge bg="#EEF2FF" color="#4338CA" borderRadius="full" px="10px" py="4px">
                          Protegido
                        </Badge>
                      )}
                    </Flex>
                    <Text mt="6px" fontSize="13px" color="#64748B">
                      {permissionSections.filter(section => Number(roleItem[section.key] || 0) > 0).length}{' '}
                      paginas com acesso liberado
                    </Text>
                  </Box>

                  {canManage && (
                    <HStack spacing="6px">
                      <IconButton
                        aria-label="Editar cargo"
                        borderRadius="14px"
                        bg="#F8FAFC"
                        icon={<Icon as={IoPencilOutline} color="#334155" fontSize="18px" />}
                        onClick={() => navigate(`/cargo/${roleItem.id}`)}
                        isDisabled={roleItem.name === 'Proprietário'}
                      />
                      <IconButton
                        aria-label="Excluir cargo"
                        borderRadius="14px"
                        bg="#FFF1F2"
                        icon={<Icon as={IoTrashOutline} color="#BE123C" fontSize="18px" />}
                        onClick={() => openDialog(roleItem.id)}
                        isDisabled={roleItem.name === 'Proprietário'}
                      />
                    </HStack>
                  )}
                </Flex>

                <SimpleGrid columns={{ base: 1, md: 2 }} spacing="10px">
                  {permissionSections.map(section => {
                    const currentStatus = Number(roleItem[section.key] || 0);
                    const statusColor = getRoleStatusColor(currentStatus);

                    return (
                      <Flex
                        key={section.key}
                        justify="space-between"
                        align="center"
                        p="12px"
                        borderRadius="16px"
                        bg="#F8FAFC"
                        border="1px solid #E2E8F0"
                      >
                        <Text fontSize="13px" color="#475569" fontWeight="600">
                          {section.label}
                        </Text>
                        <Badge
                          borderRadius="full"
                          px="10px"
                          py="4px"
                          bg={statusColor.bg}
                          color={statusColor.color}
                        >
                          {getRoleStatusLabel(currentStatus)}
                        </Badge>
                      </Flex>
                    );
                  })}
                </SimpleGrid>
              </Box>
            ))
          ) : (
            <Box
              gridColumn="1 / -1"
              bg="white"
              borderRadius="24px"
              p="32px"
              border="1px dashed #CBD5E1"
              textAlign="center"
              color="#64748B"
            >
              Nenhum cargo encontrado com os filtros atuais.
            </Box>
          )}
        </Grid>
      </Flex>
    </HeaderSideBar>
  );
}
